export const MEMBERSHIP_THEME_TOKENS = [
  'standard',
  'silver',
  'gold',
  'platinum',
  'onyx',
  'bronze',
  'emerald',
  'ruby',
  'sapphire',
  'obsidian',
  'pearl',
] as const;

export type MembershipThemeToken = typeof MEMBERSHIP_THEME_TOKENS[number];
export type MembershipThemeVisual = {
  token: MembershipThemeToken;
  label: string;
  swatchHex: string;
  cardBackground: string;
  accentRing: string;
  textMode: 'light' | 'dark';
  // Exact tier text/accent colors — kept identical to the mobile store-detail
  // membership card palette (COLORS.membership[token].text / .accent) so the
  // merchant portal renders memberships the exact same way. The 135deg gradient
  // matches the mobile card's top-left → bottom-right diagonal.
  textHex: string;
  accentHex: string;
};

const MEMBERSHIP_THEME_TOKEN_SET = new Set<string>(MEMBERSHIP_THEME_TOKENS);

export const MEMBERSHIP_THEME_VISUALS: Record<MembershipThemeToken, MembershipThemeVisual> = {
  standard: {
    token: 'standard',
    label: 'Standard',
    swatchHex: '#262626',
    cardBackground: 'linear-gradient(135deg, #262626, #151515)',
    accentRing: 'rgba(140,140,140,0.24)',
    textMode: 'light',
    textHex: '#FFFFFF',
    accentHex: '#8C8C8C',
  },
  silver: {
    token: 'silver',
    label: 'Silver',
    swatchHex: '#767E89',
    cardBackground: 'linear-gradient(135deg, #767E89, #4B525C)',
    accentRing: 'rgba(207,213,221,0.24)',
    textMode: 'light',
    textHex: '#FFFFFF',
    accentHex: '#CFD5DD',
  },
  gold: {
    token: 'gold',
    label: 'Gold',
    swatchHex: '#D7B45A',
    cardBackground: 'linear-gradient(135deg, #D7B45A, #A9842F)',
    accentRing: 'rgba(240,220,160,0.24)',
    textMode: 'dark',
    textHex: '#1F1707',
    accentHex: '#F0DCA0',
  },
  platinum: {
    token: 'platinum',
    label: 'Platinum',
    swatchHex: '#C2C8D0',
    cardBackground: 'linear-gradient(135deg, #C2C8D0, #939BA6)',
    accentRing: 'rgba(237,239,243,0.24)',
    textMode: 'dark',
    textHex: '#14181E',
    accentHex: '#EDEFF3',
  },
  onyx: {
    token: 'onyx',
    label: 'Onyx',
    swatchHex: '#1E1C1B',
    cardBackground: 'linear-gradient(135deg, #1E1C1B, #0C0B0A)',
    accentRing: 'rgba(76,74,71,0.24)',
    textMode: 'light',
    textHex: '#FFFFFF',
    accentHex: '#4C4A47',
  },
  bronze: {
    token: 'bronze',
    label: 'Bronze',
    swatchHex: '#A06B38',
    cardBackground: 'linear-gradient(135deg, #A06B38, #5E3F22)',
    accentRing: 'rgba(224,180,131,0.24)',
    textMode: 'light',
    textHex: '#FFFFFF',
    accentHex: '#E0B483',
  },
  emerald: {
    token: 'emerald',
    label: 'Emerald',
    swatchHex: '#15705A',
    cardBackground: 'linear-gradient(135deg, #15705A, #073B2D)',
    accentRing: 'rgba(87,201,160,0.24)',
    textMode: 'light',
    textHex: '#FFFFFF',
    accentHex: '#57C9A0',
  },
  ruby: {
    token: 'ruby',
    label: 'Ruby',
    swatchHex: '#9B2740',
    cardBackground: 'linear-gradient(135deg, #9B2740, #5E1421)',
    accentRing: 'rgba(229,132,150,0.24)',
    textMode: 'light',
    textHex: '#FFFFFF',
    accentHex: '#E58496',
  },
  sapphire: {
    token: 'sapphire',
    label: 'Sapphire',
    swatchHex: '#2C4C8F',
    cardBackground: 'linear-gradient(135deg, #2C4C8F, #16294F)',
    accentRing: 'rgba(127,166,224,0.24)',
    textMode: 'light',
    textHex: '#FFFFFF',
    accentHex: '#7FA6E0',
  },
  obsidian: {
    token: 'obsidian',
    label: 'Obsidian',
    swatchHex: '#181A1E',
    cardBackground: 'linear-gradient(135deg, #181A1E, #070809)',
    accentRing: 'rgba(58,61,68,0.24)',
    textMode: 'light',
    textHex: '#FFFFFF',
    accentHex: '#3A3D44',
  },
  pearl: {
    token: 'pearl',
    label: 'Pearl',
    swatchHex: '#ECEAF1',
    cardBackground: 'linear-gradient(135deg, #ECEAF1, #C9CFDA)',
    accentRing: 'rgba(154,163,178,0.24)',
    textMode: 'dark',
    textHex: '#1A1A1A',
    accentHex: '#9AA3B2',
  },
};

const normalizeThemeTokenKey = (value: unknown): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

export const normalizeMembershipThemeToken = (value: unknown): MembershipThemeToken => {
  const normalized = normalizeThemeTokenKey(value);
  if (MEMBERSHIP_THEME_TOKEN_SET.has(normalized)) {
    return normalized as MembershipThemeToken;
  }
  const collapsed = normalized.replace(/_/g, '');
  const collapsedMatch = MEMBERSHIP_THEME_TOKENS.find((token) => token.replace(/_/g, '') === collapsed);
  if (collapsedMatch) {
    return collapsedMatch;
  }
  return 'standard';
};

export const resolveThemeToken = (value: unknown): MembershipThemeVisual =>
  MEMBERSHIP_THEME_VISUALS[normalizeMembershipThemeToken(value)];

/**
 * Canonical customer membership-card surface — a near-black base with the tier
 * colour tinted in from the top, matching the mobile app exactly (gold reads as
 * "mostly dark with a gold tint", emerald as dark with an emerald tint, etc.).
 * Use for the actual customer-facing card; keep `cardBackground` (the solid tier
 * gradient) for the small theme-picker swatches so tiers stay distinguishable.
 */
export const membershipCardSurface = (theme: MembershipThemeVisual): string => {
  const normalized = String(theme.swatchHex || '').replace('#', '').trim();
  const safe = /^[0-9a-fA-F]{6}$/.test(normalized) ? normalized : '262626';
  const r = Number.parseInt(safe.slice(0, 2), 16);
  const g = Number.parseInt(safe.slice(2, 4), 16);
  const b = Number.parseInt(safe.slice(4, 6), 16);
  return (
    `radial-gradient(135% 95% at 72% -12%, ` +
    `rgba(${r}, ${g}, ${b}, 0.45) 0%, ` +
    `rgba(${r}, ${g}, ${b}, 0.12) 34%, ` +
    `rgba(${r}, ${g}, ${b}, 0) 60%), #0C0C0C`
  );
};
